import React from 'react';
import { Link } from 'react-router-dom';
import { Drum, Music, Youtube } from 'lucide-react';
import VisualizerBackground from '../components/VisualizerBackground';
import Visualizer from '../components/Visualizer';
import YoutubeEmbed from '../components/YoutubeEmbed';
import ServiceCard from '../components/ServiceCard';
import ScrollAnimation from '../components/ScrollAnimation';
import ParallaxSection from '../components/ParallaxSection';
import videoData from '../data/videos.json';
import { venuePhotos } from '../data/photos';
import OptimizedImage from '../components/OptimizedImage';
import RecentTestimonials from '../components/RecentTestimonials';

const Index = () => {
  const featuredVideos = videoData.featured;

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="min-h-screen bg-drummer-dark text-white">
      <VisualizerBackground />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-16">
        <div className="container mx-auto px-4 py-16 md:py-20 lg:py-24 relative z-10">
          <div className="flex flex-col items-center text-center">
            <Visualizer className="mb-6" barCount={15} />
            <ScrollAnimation animation="fadeUp">
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-client-yellow via-client-yellow to-client-teal animate-gradient-x">Precision Behind</span>
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-client-yellow via-client-yellow to-client-teal animate-gradient-x">
                  The Beat
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-white max-w-3xl mb-8">
                Professional drummer, creative director, and content creator pushing the boundaries 
                of musical expression and visual storytelling. Bringing technical precision and 
                artistic vision to every project.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link 
                  to="/contact" 
                  className="drum-button"
                  title="Contact Daryian for collaboration"
                  aria-label="Contact Daryian for collaboration opportunities"
                >
                  Collaborate Now
                </Link>
                <Link 
                  to="/videos" 
                  className="drum-button-outline"
                  title="View Daryian's video performances"
                  aria-label="View Daryian's video performances and content"
                >
                  Watch Performances
                </Link>
              </div>
            </ScrollAnimation>
          </div>
        </div>
        <div className="absolute bottom-10 left-0 right-0 text-center">
          <a href="#about" className="inline-block animate-bounce" aria-label="Scroll down to About Daryian section">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white/70" aria-hidden="true"><path d="M12 5v14M5 12l7 7 7-7"/></svg>
            <span className="sr-only">Scroll to About section</span>
          </a>
        </div>
      </section>
      
      {/* About Section */}
      <section id="about" className="py-8 md:py-12 relative">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <ScrollAnimation>
              <h2 className="section-heading">About Daryian</h2>
            </ScrollAnimation>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <ScrollAnimation animation="slideRight" delay={0.2}>
              <div className="electric-border">
                <div className="w-full aspect-w-4 aspect-h-3 bg-gray-800 rounded-lg overflow-hidden">
                  <OptimizedImage 
                    src={venuePhotos.milestone.second}
                    alt={venuePhotos.milestone.alt}
                    className="w-full h-full object-cover"
                    width={1200}
                    height={900}
                  />
                </div>
              </div>
            </ScrollAnimation>
            <ScrollAnimation animation="slideLeft" delay={0.4}>
              <div>
                <p className="text-xl text-white mb-6">
                  Daryian Kelton is more than just a drummer. He's a creative force who brings the rhythm, precision, 
                  and passion from his musical background into every artistic medium he explores.
                </p>
                <p className="text-xl text-white mb-6">
                  As a musician, photographer, and videographer, Daryian shows his passion for the arts by using 
                  the lessons he's learned from one medium and applying them to others.
                </p>
                <p className="text-xl text-white mb-8">
                  Through his lifestyle vlogs, Daryian's passions all come together, creating a unique window into 
                  the life and mind of a multi-talented artist.
                </p>
                <Link 
                  to="/about" 
                  className="drum-button"
                  title="Learn more about Daryian's story"
                  aria-label="Learn more about Daryian's background and artistic journey"
                >
                  Discover Daryian's Story
                </Link>
              </div>
            </ScrollAnimation>
          </div>
        </div>
        <ParallaxSection className="absolute top-1/4 right-0 w-64 h-64 bg-drummer-purple/10 rounded-full blur-3xl -z-10" speed={-0.3}>
          <div className="w-full h-full" />
        </ParallaxSection>
      </section>
      
      {/* Featured Videos */}
      <section className="py-8 md:py-12 relative">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <ScrollAnimation>
              <h2 className="section-heading">Featured Videos</h2>
            </ScrollAnimation>
          </div>
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            {featuredVideos.map((video, index) => (
              <ScrollAnimation key={index} animation="fadeUp" delay={0.1 * index}>
                <div className="flex flex-col">
                  <YoutubeEmbed 
                    videoId={video.id} 
                    title={video.title}
                    className="w-full aspect-video"
                  />
                  <h3 className="text-lg font-bold mt-2 mb-1">{video.title}</h3>
                </div>
              </ScrollAnimation>
            ))}
          </div>
          <ScrollAnimation animation="scaleUp" delay={0.3}>
            <div className="mt-6 text-center">
              <Link 
                to="/videos" 
                className="drum-button"
                title="View all of Daryian's videos"
                aria-label="View all of Daryian's video content and performances"
                onClick={scrollToTop}
              >
                View All Videos
              </Link>
            </div>
          </ScrollAnimation>
        </div>
        <ParallaxSection className="absolute top-1/3 left-0 w-96 h-96 bg-drummer-blue/10 rounded-full blur-3xl -z-10" speed={0.3}>
          <div className="w-full h-full" />
        </ParallaxSection>
      </section>
      
      {/* Services Section */}
      <section className="py-8 md:py-12 relative">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <ScrollAnimation>
              <h2 className="section-heading">Professional Services</h2>
              <p className="text-xl text-white max-w-3xl mx-auto text-center mb-8">
                Specialized offerings from a dedicated professional who brings passion, 
                precision, and creativity to every project.
              </p>
            </ScrollAnimation>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <ScrollAnimation animation="fadeUp" delay={0.1}>
              <ServiceCard 
                title="Session Drummer"
                description="Expert drum performances for recording sessions, live events, and studio work. Bringing rhythm, energy, and precision to your musical projects."
                icon={<Drum size={28} />}
                serviceId="session-drummer"
              />
            </ScrollAnimation>
            <ScrollAnimation animation="fadeUp" delay={0.2}>
              <ServiceCard 
                title="Music Production"
                description="Full-spectrum music production services from arrangement to final mix. Elevate your sound with professional quality and creative direction."
                icon={<Music size={28} />}
                serviceId="music-production"
              />
            </ScrollAnimation>
            <ScrollAnimation animation="fadeUp" delay={0.3}>
              <ServiceCard 
                title="Content Creation"
                description="Professional video and photo production with a musician's sensibility. Capturing the perfect moment with technical skill and artistic vision."
                icon={<Youtube size={28} />}
                serviceId="content-creation"
              />
            </ScrollAnimation>
          </div>
          <ScrollAnimation animation="fadeIn" delay={0.4}>
            <div className="mt-8 text-center">
              <Link 
                to="/services" 
                className="drum-button"
                title="Explore Daryian's professional services"
                aria-label="Explore all of Daryian's professional services and offerings"
                onClick={scrollToTop}
              >
                Explore All Services
              </Link>
            </div>
          </ScrollAnimation>
        </div>
      </section>
      
      {/* Sponsors Section */}
      <section className="py-8 md:py-12 relative">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <ScrollAnimation>
              <h2 className="section-heading">Sponsors</h2>
            </ScrollAnimation>
          </div>
          <div className="max-w-4xl mx-auto">
            <ScrollAnimation animation="scaleUp" delay={0.2}>
              <div className="glassmorphic p-6 md:p-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-8">
                  <div className="flex items-center space-x-3">
                    <OptimizedImage
                      src="/images/sponsors/@dopestickz-logo.png"
                      alt="DopeStickz Logo"
                      width={120}
                      height={48}
                      className="h-12 w-auto object-contain"
                    />
                    <div className="text-left">
                      <h3 className="text-lg font-bold text-white">DopeStickz</h3>
                      <p className="text-white text-sm">Official Drumstick Sponsor</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <OptimizedImage
                      src="/images/sponsors/@TRX-logo.png"
                      alt="TRX Cymbals Logo"
                      width={120}
                      height={48}
                      className="h-12 w-auto object-contain"
                    />
                    <div className="text-left">
                      <h3 className="text-lg font-bold text-white">TRX Cymbals</h3>
                      <p className="text-white text-sm">Official Cymbal Sponsor</p>
                    </div>
                  </div>
                </div>
                <div className="mt-8 text-center">
                  <h3 className="text-xl font-bold mb-2">Interested in Sponsorship?</h3>
                  <p className="text-gray-300 mb-4 max-w-2xl mx-auto">
                    Collaborate with Daryian and reach a passionate audience of music lovers and creators.
                  </p>
                  <Link 
                    to="/contact?service=sponsorship" 
                    className="drum-button"
                    title="Become a sponsor of Daryian's work"
                    aria-label="Contact Daryian to become a sponsor"
                    onClick={scrollToTop}
                  >
                    Become a Sponsor
                  </Link>
                </div>
              </div>
            </ScrollAnimation>
          </div>
        </div>
      </section>
      
      {/* Testimonials */}
      <section className="py-8 md:py-12 relative bg-black/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <ScrollAnimation>
              <h2 className="section-heading">Client Testimonials</h2>
            </ScrollAnimation>
          </div>
          <RecentTestimonials className="max-w-5xl mx-auto" />
        </div>
        <ParallaxSection className="absolute bottom-0 right-0 w-80 h-80 bg-drummer-purple/10 rounded-full blur-3xl -z-10" speed={0.2}>
          <div className="w-full h-full" />
        </ParallaxSection>
      </section>
      
      {/* Call to Action */}
      <section className="py-8 md:py-12 relative mb-6">
        <div className="container mx-auto px-4">
          <ScrollAnimation animation="fadeUp">
            <div className="glassmorphic p-8 md:p-12 lg:p-16 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-drummer-purple/20 to-drummer-blue/20 -z-10" />
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Create Something Amazing?</h2>
                <p className="text-xl text-white mb-8">
                  Let's collaborate on your next project. Whether you need music, photography, or video content, 
                  let's bring your vision to life.
                </p>
                <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                  <Link 
                    to="/contact" 
                    className="drum-button"
                    title="Contact Daryian for your next project"
                    aria-label="Contact Daryian to discuss your next project"
                    onClick={scrollToTop}
                  >
                    Get In Touch
                  </Link>
                  <Link 
                    to="/services" 
                    className="drum-button-outline"
                    title="Explore Daryian's services"
                    aria-label="View all of Daryian's professional services" 
                    onClick={scrollToTop}
                  >
                    View Services
                  </Link>
                </div>
              </div>
            </div>
          </ScrollAnimation>
        </div>
      </section>
    </div>
  );
};

export default Index;
